import {
  HealthSchema,
  MarketsResponseSchema,
  MoversResponseSchema,
  MarketCatalystEventsResponseSchema,
  RecentQuakeEventsResponseSchema,
  RecentCatalystEventsResponseSchema,
  RelatedMarketsResponseSchema,
  StatsSchema,
  SummarySchema,
  TodayQuakeEventsResponseSchema,
  type Health,
  type MarketsResponse,
  type MarketCatalystEventsResponse,
  type MoversResponse,
  type RecentCatalystEventsResponse,
  type RecentQuakeEventsResponse,
  type RelatedMarketsResponse,
  type Stats,
  type Summary,
  type TodayQuakeEventsResponse,
} from './types';
import type { DashboardFilters } from '../lib/filters';

const API_BASE = stripTrailingSlash(import.meta.env.VITE_API_BASE_URL ?? '');
const WS_BASE = stripTrailingSlash(import.meta.env.VITE_WS_BASE_URL ?? '');
const BUILD_LABEL = import.meta.env.VITE_BUILD_LABEL ?? import.meta.env.VITE_APP_VERSION ?? 'local';

export function apiBaseURL() {
  if (API_BASE) return API_BASE;
  return window.location.origin;
}

export function buildLabel() {
  return BUILD_LABEL;
}

export async function getHealth({ signal }: { signal?: AbortSignal } = {}): Promise<Health> {
  const json = await getJSON('/healthz', undefined, signal);
  return HealthSchema.parse(json);
}

export async function getStats({ signal }: { signal?: AbortSignal } = {}): Promise<Stats> {
  const json = await getJSON('/api/v1/stats', undefined, signal);
  return StatsSchema.parse(json);
}

export async function getSummary({ signal }: { signal?: AbortSignal } = {}): Promise<Summary> {
  const json = await getJSON('/api/v1/summary', undefined, signal);
  return SummarySchema.parse(json);
}

export async function getMovers(
  filters: DashboardFilters,
  { signal, limit = 20 }: { signal?: AbortSignal; limit?: number } = {},
): Promise<MoversResponse> {
  const params = makeMoverParams(filters, limit);
  const json = await getJSON('/api/v1/movers', params, signal);
  return MoversResponseSchema.parse(json);
}

export async function getMarkets(
  params: { search?: string; category?: string; limit?: number },
  { signal }: { signal?: AbortSignal } = {},
): Promise<MarketsResponse> {
  const query = new URLSearchParams({ limit: String(params.limit ?? 100) });
  const search = params.search?.trim();
  if (search) query.set('search', search);
  if (params.category && params.category !== 'all') query.set('category', params.category);
  const json = await getJSON('/api/v1/markets', query, signal);
  return MarketsResponseSchema.parse(json);
}

export async function getRecentQuakeEvents(
  limit = 50,
  { signal }: { signal?: AbortSignal } = {},
): Promise<RecentQuakeEventsResponse> {
  const params = new URLSearchParams({ limit: String(limit) });
  const json = await getJSON('/api/v1/quakes/recent', params, signal);
  return RecentQuakeEventsResponseSchema.parse(json);
}

export async function getTodayQuakeEvents(
  limit = 100,
  { signal }: { signal?: AbortSignal } = {},
): Promise<TodayQuakeEventsResponse> {
  const params = new URLSearchParams({ limit: String(limit) });
  const json = await getJSON('/api/v1/quakes/today', params, signal);
  return TodayQuakeEventsResponseSchema.parse(json);
}

export async function getRecentCatalystEvents(
  limit = 20,
  { signal }: { signal?: AbortSignal } = {},
): Promise<RecentCatalystEventsResponse> {
  const params = new URLSearchParams({ limit: String(limit) });
  const json = await getJSON('/api/v1/catalysts/recent', params, signal);
  return RecentCatalystEventsResponseSchema.parse(json);
}

export async function getMarketCatalystEvents(
  marketID: string,
  limit = 10,
  { signal }: { signal?: AbortSignal } = {},
): Promise<MarketCatalystEventsResponse> {
  const params = new URLSearchParams({ limit: String(limit) });
  const json = await getJSON(`/api/v1/markets/${encodeURIComponent(marketID)}/catalysts`, params, signal);
  return MarketCatalystEventsResponseSchema.parse(json);
}

export async function getRelatedMarkets(
  marketID: string,
  window: string,
  limit = 10,
  { signal }: { signal?: AbortSignal } = {},
): Promise<RelatedMarketsResponse> {
  const params = new URLSearchParams({ window, limit: String(limit) });
  const json = await getJSON(`/api/v1/markets/${encodeURIComponent(marketID)}/related`, params, signal);
  return RelatedMarketsResponseSchema.parse(json);
}

export function liveMoversURL(filters: DashboardFilters) {
  const params = makeMoverParams(filters, 20);
  const path = `/api/v1/live?${params.toString()}`;
  if (WS_BASE) return `${WS_BASE}${path}`;
  if (API_BASE) return `${API_BASE.replace(/^http/, 'ws')}${path}`;
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}${path}`;
}

function makeMoverParams(filters: DashboardFilters, limit: number) {
  const params = new URLSearchParams({
    window: filters.window,
    mode: filters.mode,
    limit: String(limit),
    min_score: '0',
  });
  if (filters.category !== 'all') params.set('category', filters.category);
  if (filters.query.trim()) params.set('q', filters.query.trim());
  return params;
}

async function getJSON(path: string, params?: URLSearchParams, signal?: AbortSignal) {
  const qs = params ? `?${params.toString()}` : '';
  const init: RequestInit = {
    headers: { Accept: 'application/json' },
  };
  if (signal) init.signal = signal;

  const response = await fetch(`${API_BASE}${path}${qs}`, init);
  if (!response.ok) {
    throw new Error(`GET ${path} failed: ${response.status}`);
  }
  return response.json() as Promise<unknown>;
}

function stripTrailingSlash(value: string) {
  return value.endsWith('/') ? value.slice(0, -1) : value;
}
